import {useState} from 'react';

import {Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {AfriexCurrencyCode, RatesMap, SupportedAssets} from '@/types';
import {formatValues} from '@/utils/dashboard';

type RatesTableProp = {
  rates: RatesMap;
  onRatesEdited?: (
    fromCurrency: AfriexCurrencyCode,
    toCurrency: AfriexCurrencyCode,
    newRate: number,
  ) => void;
  isEditable?: boolean;
  /**
   * Test hook: the table renders it as its container's `data-cy`, and each rate
   * cell as `<dataCy>-cell`, carrying its corridor in `data-base` and
   * `data-quote`.
   */
  dataCy?: string;
};

export const ratesHeaderHue = 'bg-blue-50 p-8 shadow-sm sticky';

const RatesTable = ({rates, onRatesEdited, dataCy}: RatesTableProp) => {
  return (
    <Table containerDataCy={dataCy}>
      <Thead className={ratesHeaderHue}>
        <Tr enableHover={false}>
          <Th className="px-2"></Th>
          {Object.keys(rates)?.map((currencySymbol) => (
            <Th key={currencySymbol} className="px-2">
              {currencySymbol?.trim()}
            </Th>
          ))}
        </Tr>
      </Thead>
      <TBody>
        {Object.keys(rates).map((baseCurrency) => (
          <RatesTableRow
            key={baseCurrency}
            rates={rates}
            baseCurrency={baseCurrency}
            onRatesEdited={onRatesEdited}
            dataCy={dataCy}
          />
        ))}
      </TBody>
    </Table>
  );
};
const RatesTableRow = ({
  rates,
  baseCurrency,
  onRatesEdited,
  dataCy,
}: {
  rates: RatesMap;
  baseCurrency: string;
  onRatesEdited: RatesTableProp['onRatesEdited'];
  dataCy?: string;
}) => {
  const textClassName = 'text-sm text-gray-800';
  return (
    <Tr className="hover:bg-gray-100 transition-colors duration-300">
      <Td
        className={`sticky left-0 bg-gray-200 dark:bg-gray-900 px-2 py-4 font-medium ${textClassName}`}
      >
        {baseCurrency}
      </Td>

      {Object.keys(rates).map((quoteCurrency: string, index: number) => (
        <RateCell
          key={index}
          rate={
            rates?.[baseCurrency as SupportedAssets]?.[
              quoteCurrency as SupportedAssets
            ] ?? 0
          }
          baseCurrency={baseCurrency}
          quoteCurrency={quoteCurrency}
          isEditable={true}
          onRatesEdited={onRatesEdited}
          dataCy={dataCy}
        />
      ))}
    </Tr>
  );
};

const RateCell = ({
  rate,
  baseCurrency,
  quoteCurrency,
  dataCy,
}: {
  // `RatesValueMap` leaves are wire strings, and the `?? 0` fallback is a
  // number, so a cell holds either. The `Number(...)` calls below are what the
  // removed `any` was papering over.
  rate: string | number;
  baseCurrency: string;
  quoteCurrency: string;
  onRatesEdited: RatesTableProp['onRatesEdited'];
  isEditable: RatesTableProp['isEditable'];
  dataCy?: string;
}) => {
  const [isControlsVisible, setIsControlsVisible] = useState(false);
  const textClassName = 'text-sm text-gray-800';
  const title =
    Number(rate) > 0 ? `1 ${baseCurrency} = ${rate} ${quoteCurrency}` : '';

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (e.detail > 0) {
      setIsControlsVisible(true);
    }
  };

  return (
    <Td
      data-cy={dataCy ? `${dataCy}-cell` : undefined}
      data-base={dataCy ? baseCurrency : undefined}
      data-quote={dataCy ? quoteCurrency : undefined}
      className={`px-2 py-4 text-left whitespace-nowrap ${textClassName} ${
        isControlsVisible ? 'border border-blue-500' : ''
      }`}
    >
      <div className="flex justify-between" title={title} onClick={handleClick}>
        {formatValues(Number(rate), 'rate', 6)}
      </div>
    </Td>
  );
};

export default RatesTable;
